/**
 * Histories-pages slice (SCRUM-1014) — createSlice/createAsyncThunk migration of
 * historiesPagesReducer + historiesPagesAction. First worked example of the
 * bare-RTK standard (SCRUM-652,
 * P2CMS/docs/Client_State_And_Data_Fetching_Standard.md); mirrors
 * pageServices/pagesSlice.ts (SCRUM-722).
 *
 * The legacy import path ./historiesPagesAction re-binds everything exported
 * here (a re-export shim), so existing importers keep working unchanged:
 * UpdatePageContainer/UpdatePageContent (addHistoriesPages), the HistoriesPages
 * screens, and the SCRUM-755/840 tests. New code should import from this module.
 *
 * Behaviour-preserving migration. Contracts kept verbatim:
 * - deleteHistoryPageOperation / activateHistoryPageOperation stay
 *   createAppAsyncThunk (SCRUM-840): they resolve to the id, reject on failure,
 *   and the central appErrorMiddleware feeds GET_ERRORS. Their list removal now
 *   rides .fulfilled in extraReducers instead of a manual DELETE_HISTORIES_PAGE
 *   dispatch (writeThunks.errorHandling.test.js pins the resolve/reject + hub
 *   behaviour, not the removal action).
 * - addHistoriesPages / updateHistoryPageContent keep their legacy resolve/reject
 *   contracts via thin .unwrap() wrappers so callers still branch on success
 *   (finishUpdating.redirectGuard + addHistoriesPages.errorHandling, SCRUM-755).
 *   rejectWithValue(err) carries the *original* axios error so the callers can
 *   read err.response.data.message.
 * - getHistoriesPagesByCurrentSite never rejects: the list becomes [] on a failed
 *   fetch, matching the legacy catch.
 * - getByIDHistoryPageOperation stays a PLAIN createAsyncThunk (not App-): a
 *   failed load must reject to its caller so UpdateHistoriesPages' mount effect
 *   catches it and shows the SCRUM-957 load-error — WITHOUT the central hub also
 *   toasting (there was no hub toast before). The caller awaits .unwrap().
 *
 * The `errors` field + the GET_ERRORS shadowing case are preserved verbatim.
 * They are dead (SCRUM-957 Finding 4), but removing them is gated on Finding 2's
 * open decision, so this behaviour-preserving migration leaves them in place.
 */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { AxiosResponse } from "axios";
import type {
  HistoriesPageActivateResponse,
  HistoriesPageAddResponse,
  HistoriesPageByIdResponse,
  HistoriesPagesBySiteResponse,
  HistoriesPageUpdateContentResponse,
} from "../../../../shared/apiTypes";
import api from "../api";
import createAppAsyncThunk from "../createAppAsyncThunk";
import { GET_ERRORS } from "../errorServices/errorTypes";
import { asCaught } from "../caughtError";

type HistoriesPagesState = {
  histories_pages: any[];
  histories_page: any;
  errors: any;
  message: string;
};

const initialState: HistoriesPagesState = {
  histories_pages: [],
  // Legacy quirk kept: starts as [] but then holds the loaded history-page object.
  histories_page: [],
  errors: "",
  message: "",
};

// ---------------------------------------------------------------------------
// Async thunks. Each mirrors its legacy historiesPagesAction block; the state
// writes ride .fulfilled in the slice below.
// ---------------------------------------------------------------------------

// Read — never rejects: the list resets to [] on a failed fetch (legacy catch).
export const getHistoriesPagesByCurrentSite = createAsyncThunk(
  "historiesPages/getByCurrentSite",
  async () => {
    try {
      const res: AxiosResponse<HistoriesPagesBySiteResponse> = await api.get("/api/historiespages/pagesBySite");
      return res.data;
    } catch {
      return [];
    }
  }
);

// Read — PLAIN createAsyncThunk on purpose (see header): rejects to the caller on
// failure so UpdateHistoriesPages' mount effect catches it (SCRUM-957), with no
// central-hub toast. A successful load clears the global GET_ERRORS sentinel,
// exactly as the legacy thunk did.
export const getByIDHistoryPageOperation = createAsyncThunk(
  "historiesPages/getById",
  async (id: string, { dispatch }) => {
    const res: AxiosResponse<HistoriesPageByIdResponse> = await api.get(`/api/historiespages/pageById/${id}`);
    dispatch({ type: GET_ERRORS, payload: {} });
    return res.data;
  }
);

// Writes (SCRUM-840 pilot) — stay createAppAsyncThunk: resolve to the id, reject
// on failure, central hub surfaces the error. Removal rides .fulfilled below.
export const deleteHistoryPageOperation = createAppAsyncThunk(
  "historiesPages/delete",
  async (id: string) => {
    await api.delete<void>(`/api/historiespages/delete/${id}`);
    return id;
  }
);

// SCRUM-1629: the route answers 201 when the version went live and 202 when a
// non-administrator's activation was queued for review instead (SCRUM-1627).
// The screen needs the status and the server's message to tell the editor
// which of the two happened, so the thunk resolves with both; the reducer
// below drops the row only when the version went live -- on 202 the server
// kept it, so the list keeps it too.
export const activateHistoryPageOperation = createAppAsyncThunk(
  "historiesPages/activate",
  async (id: string) => {
    const res: AxiosResponse<HistoriesPageActivateResponse> = await api.put(`/api/historiespages/activate/${id}`);
    // 201 answers the bare string "page activated!", 202 answers { msg }: only the
    // object carries a msg, and a string had none before either.
    return {
      id,
      status: res.status,
      msg: typeof res.data === "string" ? undefined : res.data?.msg,
    };
  }
);

// Write — legacy contract: resolve with res.data so the caller confirms/redirects,
// reject WITHOUT an error sentinel on failure (callers toast in their own screen
// context — SCRUM-755/883). Kept as a thin .unwrap() wrapper over the thunk.
const addHistoriesPagesThunk = createAsyncThunk(
  "historiesPages/add",
  async (data: Record<string, any>, { rejectWithValue }) => {
    try {
      const res: AxiosResponse<HistoriesPageAddResponse> = await api.post("/api/historiespages/add", data);
      return res.data;
    } catch (err) {
      // Carry the original axios error so the caller reads err.response.data.
      return rejectWithValue(err);
    }
  }
);
export const addHistoriesPages = (data: Record<string, any>) => (dispatch: any) =>
  dispatch(addHistoriesPagesThunk(data)).unwrap();

// Write — legacy contract: resolve with res.data, and on failure dispatch
// GET_ERRORS + reject (kept verbatim; the resulting double-toast is SCRUM-957
// Finding 2, out of scope for this behaviour-preserving migration).
const updateHistoryPageContentThunk = createAsyncThunk(
  "historiesPages/updateContent",
  async (data: Record<string, any>, { dispatch, rejectWithValue }) => {
    try {
      const res: AxiosResponse<HistoriesPageUpdateContentResponse> = await api.put("/api/historiespages/update/pagecontent", data);
      dispatch(getHistoriesPagesByCurrentSite());
      return res.data;
    } catch (err) {
      const errorMessage =
        asCaught(err)?.response?.data?.message || "An error occurred";
      dispatch({ type: GET_ERRORS, payload: { message: errorMessage } });
      return rejectWithValue(err);
    }
  }
);
export const updateHistoryPageContent = (data: Record<string, any>) => (dispatch: any) =>
  dispatch(updateHistoryPageContentThunk(data)).unwrap();

// Clear the loaded history page (unmount cleanup). A plain slice action now.
export const clearHistoryPage = () =>
  historiesPagesSlice.actions.historyPageCleared();

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const historiesPagesSlice = createSlice({
  name: "historiespages",
  initialState,
  reducers: {
    historyPageCleared(state) {
      state.histories_page = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getHistoriesPagesByCurrentSite.fulfilled, (state, action) => {
        state.histories_pages = action.payload;
      })
      .addCase(getByIDHistoryPageOperation.fulfilled, (state, action) => {
        state.histories_page = action.payload;
      })
      // Legacy ADD_HISTORIES_PAGES: prepend unless the page already existed.
      .addCase(addHistoriesPagesThunk.fulfilled, (state, action) => {
        state.message = action.payload.message;
        state.histories_page = action.payload.page;
        state.histories_pages = action.payload.existing
          ? state.histories_pages
          : [action.payload.page, ...state.histories_pages];
      })
      // Legacy UPDATE_CONTENT_HISTORY_PAGE: store the doc, clear the local errors.
      .addCase(updateHistoryPageContentThunk.fulfilled, (state, action) => {
        state.histories_page = action.payload;
        state.errors = null;
      })
      // Legacy DELETE_HISTORIES_PAGE: both delete and activate drop the page.
      .addCase(deleteHistoryPageOperation.fulfilled, (state, action) => {
        state.histories_pages = state.histories_pages.filter(
          (page) => page._id !== action.payload
        );
      })
      .addCase(activateHistoryPageOperation.fulfilled, (state, action) => {
        // 202 = queued for review (SCRUM-1627): the version is still pending.
        if (action.payload.status === 202) return;
        state.histories_pages = state.histories_pages.filter(
          (page) => page._id !== action.payload.id
        );
      })
      // Preserved GET_ERRORS shadowing (dead per Finding 4; kept behaviour-preserving).
      .addCase(GET_ERRORS, (state, action: PayloadAction<{ message?: string } | undefined, typeof GET_ERRORS>) => {
        state.errors = action.payload ? action.payload.message : "An error occurred";
      });
  },
});


// Exposed for tests that assert on the async action lifecycle / state.

export { initialState };
export default historiesPagesSlice.reducer;
